import { useEffect, useState } from 'react';
import { Badge, Button, Dialog, Input } from '@lognai/synapse-design-system-v2';
import { Check, Plus } from '@lognai/synapse-design-system-v2/icons';
import type { WorkspaceKpiDefinition } from './workspace-kpis';
import { workspaceToneClasses } from './workspace-module-style';
import { WorkspaceAiButton, WorkspaceAiCreator, type WorkspaceAiOptions } from './WorkspaceAiCreator';

export function WorkspaceKpiPicker({
  open,
  onOpenChange,
  definitions,
  alreadyAdded,
  onSelect,
  ai,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  definitions: readonly WorkspaceKpiDefinition[];
  alreadyAdded: readonly string[];
  onSelect: (id: string) => void;
  ai: WorkspaceAiOptions;
}) {
  const [aiMode, setAiMode] = useState(false);
  useEffect(() => { if (!open) setAiMode(false); }, [open]);
  const [search, setSearch] = useState('');
  const matches = definitions.filter((item) =>
    `${item.label} ${item.module} ${item.description}`
      .toLowerCase()
      .includes(search.toLowerCase().trim())
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={`flex max-h-[88vh] flex-col ${aiMode ? 'sm:max-w-5xl' : 'sm:max-w-2xl'}`}>
        {aiMode ? <WorkspaceAiCreator kind="kpi" {...ai} onBack={() => setAiMode(false)} onClose={() => onOpenChange(false)} /> : <>
        <Dialog.Header>
          <Dialog.Title>Add a KPI</Dialog.Title>
          <Dialog.Description>
            Choose metrics from your available modules. Removed KPIs can be added again.
          </Dialog.Description>
        </Dialog.Header>
        <WorkspaceAiButton onClick={() => setAiMode(true)} />
        <Input
          aria-label="Search KPIs"
          placeholder="Search KPIs or modules"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="grid min-h-0 gap-3 overflow-y-auto sm:grid-cols-2">
          {matches.map((item) => {
            const added = alreadyAdded.includes(item.id);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                disabled={added}
                aria-label={`Add ${item.label} KPI`}
                onClick={() => onSelect(item.id)}
                className="flex items-start gap-3 rounded-lg border border-border p-4 text-left transition hover:border-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${workspaceToneClasses[item.tone]}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">{item.module}</p>
                  <p className="text-sm font-semibold text-foreground">{item.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
                </div>
                {added ? (
                  <Badge variant="secondary" className="shrink-0">
                    <Check className="h-3 w-3" /> Added
                  </Badge>
                ) : (
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                )}
              </button>
            );
          })}
          {matches.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground sm:col-span-2">
              No matching KPIs are available for your current company and permissions.
            </p>
          )}
        </div>
        <Dialog.Footer>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Dialog.Footer>
        </>}
      </Dialog.Content>
    </Dialog>
  );
}
